import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Row } from '@tanstack/react-table';
import { PeriodStringArray } from '../../common/types';
import { filterString } from '../../common/utils/filters';
import { ValidationMessagesMap } from '../../common/utils/ValidationMessagesMap';
import { isDateRangeContained } from '../../user/utils/isRangesOverlapping';
import {
  AlternativeAlgorithmDataType,
  AlternativeAlgorithmFormDataType,
  AlternativeAlgorithmExceptionFormDataType,
  AlternativeAlgorithmSetDataType,
  AlternativeAlgorithmSetFormDataType,
} from '../schema';
import translations from '../translations/utils.translations.json';

import { AlgorithmOptions, AlgorithmSetOption } from './types';
import { AlgorithmExtendedType } from '../algorithms/schema/types';
import { AlgorithmType } from '../algorithms/schema/zAlgorithmType';
const { addTranslations, t } = i18nNsFactory('alternative-algorithm-utils');
addTranslations(translations);

enum Included {
  NotIncluded = 0,
  AnotherSet = 1,
  SameSet = 2,
  AnotherAndSameSet = 3,
}
interface AlgorithmInfo {
  groupCode?: string;
  included: Included;
}

interface AlternateForm {
  exceptions: AlternativeAlgorithmExceptionFormDataType[];
  editForm: AlternativeAlgorithmFormDataType;
}

export const alternativeFilterFn = (
  row: Row<AlternativeAlgorithmSetDataType>,
  _column: string,
  filter: any
) => {
  if (!filter) {
    return true;
  }

  const { alternativeAlgorithms } = row.original;

  return alternativeAlgorithms.some((alternative) => {
    let found = false;
    if (alternative.groupCode) {
      found = filterString(alternative.groupCode, filter);
    }
    if (!found) {
      found = filterString(alternative.code, filter);
    }

    return found;
  });
};

function alternativeToEditForm(
  alternative: AlternativeAlgorithmDataType,
  alternativeId: string
): AlternateForm {
  const { exceptions, ...editForm } = alternative;

  return {
    exceptions: exceptions.map((exception) => ({
      ...exception,
      alternativeId,
    })),
    editForm: {
      ...editForm,
      alternativeId,
    },
  };
}

export function alternativeSetToEditFormData(
  alternativeSet: AlternativeAlgorithmSetDataType
): AlternativeAlgorithmSetFormDataType {
  const { alternativeAlgorithms, ...rest } = alternativeSet;
  const alternatives: AlternativeAlgorithmFormDataType[] = [];
  const exceptions: AlternativeAlgorithmExceptionFormDataType[] = [];

  alternativeAlgorithms.forEach((alternative) => {
    const id = getAlgorithmId(alternative);
    const alternateForm = alternativeToEditForm(alternative, id);
    alternatives.push(alternateForm.editForm);
    exceptions.push(...alternateForm.exceptions);
  });

  if (!alternatives.length) {
    alternatives.push(createDefaultAlternative(false));
  }
  if (!exceptions.length) {
    exceptions.push(createDefaultException());
  }

  return {
    ...rest,
    alternativeAlgorithms: alternatives,
    exceptions,
  };
}

export function editFormToAlternativeSet(
  editForm: AlternativeAlgorithmSetFormDataType,
  idMap: Map<string, AlgorithmSetOption>
): AlternativeAlgorithmSetDataType {
  const { alternativeAlgorithms: algorithms, exceptions, ...rest } = editForm;

  const alternativeAlgorithms = algorithms
    .map((algorithm) => {
      if (!algorithm.alternativeId) {
        return null;
      }
      const algorithmId = idMap.get(algorithm.alternativeId);
      if (!algorithmId) {
        return null;
      }
      return {
        ...algorithm,
        algorithmId: algorithmId.algorithmId,
        code: algorithmId.code,
        groupCode: algorithmId.groupCode,
        exceptions: exceptions
          .filter(
            (exception) => exception.alternativeId === algorithm.alternativeId
          )
          .map((exception) => ({
            id: exception.id,
            validPeriod: exception.validPeriod,
            alternativeAlgorithmId: exception.alternativeAlgorithmId,
          })),
      } as AlternativeAlgorithmDataType;
    })
    .filter((algorithm) => !!algorithm) as AlternativeAlgorithmDataType[];

  return {
    ...rest,
    alternativeAlgorithms,
  };
}

export function createDefaultAlternativeSet(
  code: number
): AlternativeAlgorithmSetDataType {
  return {
    id: null,
    code,
    name: '',
    alternativeAlgorithms: [],
  };
}

export function createDefaultAlternative(
  defaultAlgorithm = false
): AlternativeAlgorithmFormDataType {
  return {
    id: null,
    defaultAlgorithm,
    alternativeId: null,
    alternativeAlgorithmSetId: null,
  };
}

export function createDefaultException(): AlternativeAlgorithmExceptionFormDataType {
  return {
    id: null,
    alternativeId: null,
    alternativeAlgorithmId: null,
    validPeriod: [null, null],
  };
}

function getCodeId(code?: string | null) {
  return `code-${code}`;
}

function getGroupId(groupCode: string) {
  return `group-${groupCode}`;
}

function getAlgorithmId(
  algorithm: AlgorithmExtendedType | AlternativeAlgorithmDataType
) {
  const { groupCode, code } = algorithm;
  return groupCode ? getGroupId(groupCode) : getCodeId(code);
}

export function getAlgorithmOptions(
  algorithms: AlgorithmExtendedType[]
): Map<string, AlgorithmSetOption> {
  const seenValues = new Set<string>();
  return new Map<string, AlgorithmSetOption>(
    algorithms
      .filter((option) => option.typeCode === AlgorithmType.INSERT)
      .map((algorithm) => {
        const { groupCode, code } = algorithm;

        const options: AlgorithmSetOption[] = [];
        if (groupCode) {
          options.push({
            value: getAlgorithmId(algorithm),
            label: groupCode,
            type: 'group',
            code: null,
            groupCode: groupCode,
            algorithmId: null,
          });
        }
        if (code) {
          options.push({
            value: getCodeId(code),
            label: code,
            type: 'code',
            code: code,
            groupCode: null,
            algorithmId: algorithm.id,
          });
        }

        return options;
      })
      .flat()
      .filter((option) => {
        if (seenValues.has(option.value)) {
          return false;
        }
        seenValues.add(option.value);
        return true;
      })
      .map((option) => [option.value, option])
  );
}

export function getExceptionsCount(rowData: AlternativeAlgorithmSetDataType) {
  return rowData.alternativeAlgorithms.reduce(
    (acc, alternative) => acc + (alternative.exceptions?.length ?? 0),
    0
  );
}

export function getIndexArray(count: number) {
  return Array.from(Array(count).keys());
}

export function recalculateOptions(
  values: AlternativeAlgorithmFormDataType[],
  algorithmIdMap: Map<string, AlgorithmSetOption>
): AlgorithmOptions {
  const available: AlgorithmSetOption[] = [];
  const selected: AlgorithmSetOption[] = [];
  const availableIds = new Set(algorithmIdMap.keys());

  values.forEach(
    (algorithm) =>
      algorithm.alternativeId && availableIds.delete(algorithm.alternativeId)
  );
  [...availableIds].forEach((id) => {
    const algorithmId = algorithmIdMap.get(id);
    if (algorithmId) {
      available.push(algorithmId);
    }
  });
  [...algorithmIdMap.values()].forEach((algorithm) => {
    if (!availableIds.has(algorithm.value)) {
      selected.push(algorithm);
    }
  });

  return { available, selected };
}

export function validateAlternatives(
  alternatives: AlternativeAlgorithmDataType[],
  alternativeAlgorithmSets: AlternativeAlgorithmSetDataType[],
  algorithms: AlgorithmExtendedType[]
) {
  const errors: Array<[string, string]> = [];
  if (alternatives.length < 1) {
    errors.push([
      `alternativeAlgorithms.0.alternativeId`,
      'validation.min-alternatives',
    ]);
  }
  if (!alternatives.some((alternative) => alternative.defaultAlgorithm)) {
    errors.push([
      `alternativeAlgorithms.0.defaultAlgorithm`,
      'validation.default-alternative-missing',
    ]);
  }
  const algorithmMap = new Map<string, AlgorithmInfo>();
  algorithms.forEach((algorithm) => {
    const { code, groupCode } = algorithm;
    if (groupCode) {
      algorithmMap.set(getGroupId(groupCode), {
        included: Included.NotIncluded,
      });
    }
    algorithmMap.set(getCodeId(code), {
      groupCode: groupCode === null ? undefined : groupCode,
      included: Included.NotIncluded,
    });
  });
  alternativeAlgorithmSets.forEach((set) => {
    set.alternativeAlgorithms.forEach((alternative) => {
      if (alternative.code) {
        let mapping = algorithmMap.get(getCodeId(alternative.code));
        if (mapping) {
          mapping.included = Included.AnotherSet;
          if (mapping.groupCode) {
            mapping = algorithmMap.get(getGroupId(mapping.groupCode));
            if (mapping) {
              mapping.included = Included.AnotherSet;
            }
          }
        }
      }
      if (alternative.groupCode) {
        const mapping = algorithmMap.get(getGroupId(alternative.groupCode));
        if (mapping) {
          mapping.included = Included.AnotherSet;
        }
      }
    });
  });
  alternatives.forEach((alternative, index) => {
    const id = getAlgorithmId(alternative);
    const mapping = algorithmMap.get(id);
    if (mapping) {
      const groupMapping = mapping.groupCode
        ? algorithmMap.get(getGroupId(mapping.groupCode))
        : undefined;
      const included =
        mapping.included ||
        groupMapping?.included === Included.AnotherSet ||
        groupMapping?.included === Included.AnotherAndSameSet;
      if (included) {
        errors.push([
          `alternativeAlgorithms.${index}.alternativeId`,
          included === Included.SameSet
            ? 'validation.not_unique_alt'
            : 'validation.not_unique_alt_set',
        ]);
      }

      // Algorithm / group cannot be included twice directly in the current set
      if (!mapping.included) {
        mapping.included = Included.SameSet;
      } else if (mapping.included === Included.AnotherSet) {
        mapping.included = Included.AnotherAndSameSet;
      }
    }
  });

  return errors;
}

export function validateExceptions(
  exceptions: AlternativeAlgorithmExceptionFormDataType[],
  validPeriod: PeriodStringArray
) {
  const errors: Array<[string, string]> = [];
  exceptions.forEach((exception, index) => {
    if (exception.alternativeId) {
      if (!exception.validPeriod[0]) {
        errors.push([
          `exceptions.${index}.validPeriod`,
          'validation.from-is-required',
        ]);
      }
      if (!exception.validPeriod[1]) {
        errors.push([
          `exceptions.${index}.validPeriod`,
          'validation.to-is-required',
        ]);
      }
      if (!isDateRangeContained(validPeriod, exception.validPeriod)) {
        errors.push([
          `exceptions.${index}.validPeriod`,
          'validation.exception-range',
        ]);
      }
    } else if (exception.validPeriod[0] || exception.validPeriod[1]) {
      errors.push([
        `exceptions.${index}.alternativeId`,
        'validation.exception-alternative',
      ]);
    }
  });

  return errors;
}

export function validateAlternativeSet(
  alternatives: AlternativeAlgorithmDataType[],
  exceptions: AlternativeAlgorithmExceptionFormDataType[],
  alternativeAlgorithmSets: AlternativeAlgorithmSetDataType[],
  algorithms: AlgorithmExtendedType[],
  validPeriod: PeriodStringArray
) {
  const messagesMap = new ValidationMessagesMap();
  validateAlternatives(
    alternatives,
    alternativeAlgorithmSets,
    algorithms
  ).forEach((error) => {
    messagesMap.append(error[0], t(error[1]), 'error');
  });
  validateExceptions(exceptions, validPeriod).forEach((error) => {
    messagesMap.append(error[0], t(error[1]), 'error');
  });

  return messagesMap;
}
